import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Card, DatePicker, Button, Input, message as antMessage } from 'antd';
import { IconBrain, IconSend, IconRefresh, IconChartBar, IconSnowflake, IconTruck, 
  IconClock, IconRobot, IconUser, IconBriefcase, IconPlus, IconMessageCircle, IconBolt, IconBuilding, IconCopy } from '@tabler/icons-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import dayjs from 'dayjs';
import api from '../../../authentication/axiosInstance';
import styles from './ExploreJobs.module.css';
import ReactMarkdown from 'react-markdown';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const COLORS = ['#034832', '#cdaf5e', '#FF8042', '#8884d8', '#82ca9d'];

const markdownStyles = {
  p: {
    margin: '8px 0',
    lineHeight: '1.5',
  },
  strong: {
    color: '#034832',
    fontWeight: 600,
  },
  ul: {
    margin: '8px 0',
    paddingLeft: '20px',
  },
  li: {
    margin: '4px 0',
  },
};

const ExploreJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([dayjs().subtract(90, 'days'), dayjs()]);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const chatContainerRef = useRef(null);
  const [jobStats, setJobStats] = useState({
    totalJobs: 0,
    completedJobs: 0,
    completionRate: '0',
    avgDuration: 0,
    maxDuration: 0,
    minDuration: 0,
    dailyAvg: 0,
    jobsByType: [],
    stormJobsCount: 0,
    stormPercentage: '0',
    jobsByStorm: [],
    jobsOverTime: [],
    sitesCount: 0,
    topSites: [],
    recentActivity: []
  });

  useEffect(() => {
    fetchJobs();
  }, [dateRange]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/debrief/jobs/');
      const filteredJobs = response.data.filter(job => {
        const jobDate = dayjs(job.job_start_date);
        return jobDate.isAfter(dateRange[0]) && jobDate.isBefore(dateRange[1]);
      });
      setJobs(filteredJobs);
      calculateStats(filteredJobs);
    } catch (error) {
      antMessage.error('Failed to fetch jobs data');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (jobsData) => {
    // Total jobs and completion rate
    const totalJobs = jobsData.length;
    const completedJobs = jobsData.filter(job => job.job_end_date).length;
    const completionRate = totalJobs ? (completedJobs / totalJobs * 100).toFixed(1) : '0';

    // Duration stats
    const durations = jobsData.map(job => {
      if (!job.job_start_date) return 0;
      const start = dayjs(job.job_start_date);
      const end = job.job_end_date ? dayjs(job.job_end_date) : start;
      return end.diff(start, 'hour', true);
    }).filter(duration => !isNaN(duration));

    const avgDuration = durations.length ? durations.reduce((acc, dur) => acc + dur, 0) / durations.length : 0;
    const maxDuration = durations.length ? Math.max(...durations) : 0;
    const minDuration = durations.length ? Math.min(...durations) : 0;

    // Jobs by type with percentage
    const typeCount = jobsData.reduce((acc, job) => {
      const type = job.type || 'Unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    const jobsByType = Object.entries(typeCount)
      .map(([name, value]) => ({
        name: name || 'Unknown',
        value,
        percentage: ((value / totalJobs) * 100).toFixed(1)
      }))
      .sort((a, b) => b.value - a.value);

    // Storm analysis
    const stormJobs = jobsData.filter(job => job.storm_details?.storm_type);
    const stormJobsCount = stormJobs.length;
    const stormPercentage = totalJobs ? ((stormJobsCount / totalJobs) * 100).toFixed(1) : '0';

    const stormCount = jobsData.reduce((acc, job) => {
      const stormType = job.storm_details?.storm_type || 'No Storm';
      acc[stormType] = (acc[stormType] || 0) + 1;
      return acc;
    }, {});

    const jobsByStorm = Object.entries(stormCount)
      .map(([name, value]) => ({
        name: name || 'No Storm',
        value,
        percentage: ((value / totalJobs) * 100).toFixed(1)
      }))
      .filter(storm => storm.name !== 'No Storm')
      .sort((a, b) => b.value - a.value);

    if (stormCount['No Storm']) {
      jobsByStorm.push({
        name: 'No Storm',
        value: stormCount['No Storm'],
        percentage: ((stormCount['No Storm'] / totalJobs) * 100).toFixed(1)
      });
    }

    // Time-based analysis
    const byDay = jobsData.reduce((acc, job) => {
      if (!job.job_start_date) return acc;
      const date = dayjs(job.job_start_date).format('YYYY-MM-DD');
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const dailyAvg = Object.keys(byDay).length ? 
      Object.values(byDay).reduce((acc, count) => acc + count, 0) / Object.keys(byDay).length : 0;

    const jobsOverTime = Object.entries(byDay)
      .map(([date, count]) => ({
        date,
        count,
        avg: dailyAvg
      }))
      .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));

    // Site analysis
    const uniqueSites = new Set(jobsData
      .map(job => job.site_details?.site_name)
      .filter(Boolean));
    const sitesCount = uniqueSites.size;

    const siteJobs = jobsData.reduce((acc, job) => {
      const site = job.site_details?.site_name;
      if (site) {
        acc[site] = (acc[site] || 0) + 1;
      }
      return acc;
    }, {});

    const topSites = Object.entries(siteJobs)
      .map(([name, count]) => ({
        name,
        count,
        percentage: ((count / totalJobs) * 100).toFixed(1)
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Recent activity with more context
    const recentActivity = jobsData
      .filter(job => job.job_start_date)
      .sort((a, b) => dayjs(b.job_start_date).diff(dayjs(a.job_start_date)))
      .slice(0, 5)
      .map(job => ({
        date: dayjs(job.job_start_date).format('MMM DD, YYYY'),
        type: job.type || 'Unknown',
        site: job.site_details?.site_name || 'Unknown Site',
        storm: job.storm_details?.storm_type || 'No Storm',
        duration: job.job_end_date ? 
          dayjs(job.job_end_date).diff(dayjs(job.job_start_date), 'hour', true).toFixed(1) : '0',
        staff: job.staff_details?.length || 0
      }));

    setJobStats({
      totalJobs,
      completedJobs,
      completionRate,
      avgDuration,
      maxDuration,
      minDuration,
      dailyAvg,
      jobsByType,
      stormJobsCount,
      stormPercentage,
      jobsByStorm,
      jobsOverTime,
      sitesCount,
      topSites,
      recentActivity
    });
  };

  const renderMessageContent = (content) => {
    // Detect lists
    if (content.startsWith('- ')) {
      return (
        <ul className={styles.list}>
          {content.split('\n').map((item, i) => 
            item.trim() && <li key={i}>{item.replace('- ', '')}</li>
          )}
        </ul>
      );
    }

    // Detect numbered lists
    if (content.match(/^\d+\. /)) {
      return (
        <ol className={styles.list}>
          {content.split('\n').map((item, i) => 
            item.trim() && <li key={i}>{item.replace(/^\d+\. /, '')}</li>
          )}
        </ol>
      );
    }

    // Detect key-value pairs
    if (content.includes(':') && !content.includes('\n:')) {
      const lines = content.split('\n').filter(line => line.includes(':'));
      if (lines.length > 0) {
        return (
          <div className={styles.keyValue}>
            {lines.map((line, i) => {
              const [key, ...valueParts] = line.split(':');
              const value = valueParts.join(':').trim();
              return (
                <div key={i} className={styles.keyValueRow}>
                  <span className={styles.key}>{key}:</span>
                  <span className={styles.value}>{value}</span>
                </div>
              );
            })}
          </div>
        );
      }
    }

    // Default paragraph formatting
    return content.split('\n').map((line, i) => 
      line.trim() && <p key={i} className={styles.paragraph}>{line}</p>
    );
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setSendingMessage(true);
      const userMessage = {
        role: 'user',
        content: newMessage,
        timestamp: dayjs().format('h:mm A')
      };

      setChatMessages(prev => [...prev, userMessage]);
      setNewMessage('');

      // Prepare comprehensive context for AI
      const context = {
        query: newMessage,
        jobStats: {
          overview: {
            total: jobStats.totalJobs,
            completed: jobStats.completedJobs,
            completionRate: jobStats.completionRate,
            avgDuration: jobStats.avgDuration.toFixed(1),
            dailyAvg: jobStats.dailyAvg.toFixed(1)
          },
          storms: {
            total: jobStats.stormJobsCount,
            percentage: jobStats.stormPercentage,
            distribution: jobStats.jobsByStorm
          },
          types: jobStats.jobsByType,
          sites: {
            active: jobStats.sitesCount,
            top: jobStats.topSites
          },
          timeline: jobStats.jobsOverTime,
          recent: jobStats.recentActivity
        },
        dateRange: {
          start: dateRange[0].format('YYYY-MM-DD'),
          end: dateRange[1].format('YYYY-MM-DD')
        }
      };

      const response = await api.post('/debrief/chat/', {
        message: newMessage,
        context: context
      });

      if (response.data.success) {
        const aiMessage = {
          role: 'assistant',
          content: response.data.message,
          timestamp: dayjs().format('h:mm A'),
          sources: jobStats.recentActivity
            .filter(job => job.storm_details?.storm_type)
            .slice(0, 3)
            .map(job => ({
              site: job.site,
              date: job.date,
              type: job.type,
              storm: job.storm
            }))
        };
        setChatMessages(prev => [...prev, aiMessage]);
      } else {
        antMessage.error('Failed to get AI response');
      }
    } catch (error) {
      console.error('Chat error:', error);
      antMessage.error('Error sending message');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatAIResponse = (response, context) => {
    // Format numbers and percentages
    let formattedResponse = response.replace(
      /(\d+(\.\d+)?%)|(\d+(\.\d+)?(?=\s*(?:jobs|hours|sites|h)))/g,
      match => `**${match}**`
    );

    // Remove any empty sections
    formattedResponse = formattedResponse
      .split('\n')
      .filter(line => line.trim())
      .join('\n');

    // Remove redundant headers if they're empty
    formattedResponse = formattedResponse
      .replace(/(?:Analysis|Summary|Overview):\s*(?:\n|$)/g, '')
      .replace(/Based on:\s*(?:\n|$)/g, '');

    return formattedResponse;
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const renderMessage = (msg) => (
    <div className={`${styles.message} ${msg.role === 'user' ? styles.userMessage : styles.aiMessage}`}>
      <div className={styles.messageHeader}>
        {msg.role === 'user' ? (
          <>
            <IconUser size={20} />
            <span>You</span>
          </>
        ) : (
          <>
            <IconRobot size={20} />
            <span>AI Assistant</span>
          </>
        )}
        <span className={styles.timestamp}>{msg.timestamp}</span>
        {msg.role === 'assistant' && (
          <div className={styles.messageActions}>
            <Button
              type="text"
              size="small"
              icon={<IconCopy size={16} />}
              onClick={() => {
                const textToCopy = msg.content.replace(/\*\*/g, ''); // Remove markdown ** characters
                navigator.clipboard.writeText(textToCopy)
                  .then(() => antMessage.success('Message copied to clipboard'))
                  .catch(() => antMessage.error('Failed to copy message'));
              }}
              title="Copy message"
            />
          </div>
        )}
      </div>
      <div className={styles.messageContent}>
        {msg.role === 'user' ? (
          <Typography>{msg.content}</Typography>
        ) : (
          <>
            <ReactMarkdown 
              components={{
                p: ({node, ...props}) => <Typography style={markdownStyles.p} {...props} />,
                strong: ({node, ...props}) => <span style={markdownStyles.strong} {...props} />,
                ul: ({node, ...props}) => <ul style={markdownStyles.ul} {...props} />,
                li: ({node, ...props}) => <li style={markdownStyles.li} {...props} />,
              }}
            >
              {msg.content}
            </ReactMarkdown>
            {msg.sources && (
              <div className={styles.sources}>
                <Typography variant="caption" className={styles.sourceLabel}>
                  Based on:
                </Typography>
                {msg.sources.map((source, i) => (
                  <div key={i} className={styles.sourceItem}>
                    <IconBriefcase size={14} />
                    <Typography variant="caption">{source.site} - {source.date}</Typography>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  const TypingIndicator = () => (
    <div className={`${styles.message} ${styles.aiMessage}`}>
      <div className={styles.messageHeader}>
        <IconRobot size={20} />
        <span>AI Assistant</span>
      </div>
      <div className={styles.messageContent}>
        <div className={styles.typingIndicator}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <Typography variant="h4" className={styles.title}>Jobs Analytics</Typography>
          <Typography variant="body1" className={styles.subtitle}>
            Analyze job patterns and get AI-powered insights
          </Typography>
        </div>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            className={styles.datePicker}
          />
          <Button
            type="primary"
            icon={<IconRefresh size={16} />}
            onClick={fetchJobs}
            className={styles.refreshButton}
            style={{ backgroundColor: '#034832', borderColor: '#034832' }}
          >
            Refresh Data
          </Button>
        </Box>
      </div>

      <div className={styles.content}>
        <div className={styles.mainContent}>
        <div className={styles.statsCards}>
          <Card className={styles.statCard}>
            <IconChartBar size={24} className={styles.statIcon} />
            <Typography variant="body2" className={styles.statLabel}>Total Jobs</Typography>
            <Typography variant="h5" className={styles.statValue}>
              {jobStats?.totalJobs || 0}
              <Typography variant="caption" display="block" color="textSecondary">
                {jobStats?.completionRate || 0}% Complete
              </Typography>
            </Typography>
          </Card>
          
          <Card className={styles.statCard}>
            <IconClock size={24} className={styles.statIcon} />
            <Typography variant="body2" className={styles.statLabel}>Average Duration</Typography>
            <Typography variant="h5" className={styles.statValue}>
              {(jobStats?.avgDuration || 0).toFixed(1)}h
              <Typography variant="caption" display="block" color="textSecondary">
                Range: {(jobStats?.minDuration || 0).toFixed(1)}h - {(jobStats?.maxDuration || 0).toFixed(1)}h
              </Typography>
            </Typography>
          </Card>
          
          <Card className={styles.statCard}>
            <IconSnowflake size={24} className={styles.statIcon} />
            <Typography variant="body2" className={styles.statLabel}>Storm-Related Jobs</Typography>
            <Typography variant="h5" className={styles.statValue}>
              {jobStats?.stormJobsCount || 0}
              <Typography variant="caption" display="block" color="textSecondary">
                {jobStats?.stormPercentage || 0}% of Total
              </Typography>
            </Typography>
          </Card>
          
          <Card className={styles.statCard}>
            <IconTruck size={24} className={styles.statIcon} />
            <Typography variant="body2" className={styles.statLabel}>Active Sites</Typography>
            <Typography variant="h5" className={styles.statValue}>
              {jobStats?.sitesCount || 0}
              <Typography variant="caption" display="block" color="textSecondary">
                {(jobStats?.dailyAvg || 0).toFixed(1)} Jobs/Day
              </Typography>
            </Typography>
          </Card>
        </div>

      <div className={styles.chartsContainer}>
        <Card className={styles.chartCard}>
          <Typography variant="h6" className={styles.chartTitle}>Storm Distribution</Typography>
          <div style={{ height: '300px', overflowY: 'auto' }}>
            <div style={{ minHeight: '300px', height: `${Math.max(300, jobStats.jobsByStorm.length * 40)}px` }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[...jobStats.jobsByStorm].sort((a, b) => b.value - a.value)}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    width={150}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip />
                  <Bar dataKey="value" fill="#034832">
                    {jobStats.jobsByStorm.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>

        <Card className={styles.chartCard}>
          <Typography variant="h6" className={styles.chartTitle}>Jobs by Type</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={jobStats.jobsByType}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#cdaf5e" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className={styles.chartCard}>
          <Typography variant="h6" className={styles.chartTitle}>Jobs Timeline</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={jobStats.jobsOverTime}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="count" fill="#034832" stroke="#034832" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>
        </div>

      <div className={styles.chatContainer}>
        <div className={styles.chatHeader}>
          <Typography variant="h6">AI Chat</Typography>
          <Typography variant="body2" color="textSecondary">
            Ask questions about your job data
          </Typography>
        </div>
        <div className={styles.chatMessages} ref={chatContainerRef}>
          {chatMessages.length === 0 ? (
            <div className={styles.chatPlaceholder}>
              <IconMessageCircle size={32} />
              <Typography variant="body1">
                Start a conversation about your job data
              </Typography>
              <div className={styles.quickActions}>
                <Button
                  size="small"
                  onClick={() => {
                    setNewMessage('Compare storm vs non-storm jobs');
                    sendMessage();
                  }}
                  startIcon={<IconBolt size={16} />}
                >
                  Storm Analysis
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setNewMessage('What are the busiest sites?');
                    sendMessage();
                  }}
                  startIcon={<IconBuilding size={16} />}
                >
                  Site Activity
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setNewMessage('Analyze job completion rates');
                    sendMessage();
                  }}
                  startIcon={<IconChartBar size={16} />}
                >
                  Performance
                </Button>
              </div>
            </div>
          ) : (
            <>
              {chatMessages.map((message, index) => (
                <div key={index}>{renderMessage(message)}</div>
              ))}
              {sendingMessage && <TypingIndicator />}
            </>
          )}
        </div>
        <div className={styles.inputContainer}>
          <textarea
            className={styles.inputField}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Ask about your job data..."
            rows={1}
          />
          <Button
            className={styles.sendButton}
            onClick={sendMessage}
            disabled={!newMessage.trim() || sendingMessage}
          >
            <IconSend size={20} />
          </Button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreJobs; 